import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Layout } from '../../../components/auth';
import { Button, FormFields, Input } from '../../../components/common';
import { FetchStatus } from '../../../enums';
import { useAuth, useLogin, useResetLoginStatus } from '../../../features/auth';
import { ICredentials } from '../../../interfaces';

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<ICredentials>({
    username: '',
    password: '',
  });
  const resetLoginStatus = useResetLoginStatus();
  const {
    status: { login: loginStatus },
  } = useAuth();
  const login = useLogin();

  const setUsername = (username: string) => {
    setCredentials({ ...credentials, username });
  };

  const setPassword = (password: string) => {
    setCredentials({ ...credentials, password });
  };

  const handleLogin = useCallback(() => {
    login(credentials);
  }, [credentials, login]);

  useEffect(() => {
    resetLoginStatus();
  }, []);

  useEffect(() => {
    if (loginStatus === FetchStatus.SUCCESS) {
      resetLoginStatus();
      navigate('/app/patients');
    }
  }, [loginStatus]);

  return (
    <Layout title={t('login.title')}>
      <p className="typography-paragraph-3 text-text-secondary mt-2 mb-6">
        <Trans
          i18nKey="login.subtitle"
          components={[
            <Link key="link" to="/signup" className="typography-inline-link-3 text-text-link cursor-pointer" />,
          ]}
        />
      </p>

      <FormFields>
        <Input
          value={credentials.username}
          label={t('login.field.email.label')}
          onChange={setUsername}
          placeholder={t('login.field.email.placeholder')}
          type="email"
          autoComplete="username"
        />

        <Input
          value={credentials.password}
          label={t('login.field.password.label')}
          onChange={setPassword}
          placeholder={t('login.field.password.placeholder')}
          type="password"
          autoComplete="password"
        />
      </FormFields>

      {loginStatus === FetchStatus.FAILURE ? (
        <p className="typography-paragraph-3 text-text-negative mt-6 mb-6">{t('login.validation.account')}</p>
      ) : (
        <></>
      )}

      <div className="flex mt-6">
        <Button text={t('login.button.login')} onClick={handleLogin} loading={loginStatus === FetchStatus.LOADING} />
        <Button text={t('login.button.forgotPassword')} path="/forgot-password" type="tertiary" className="ml-3" />
      </div>
    </Layout>
  );
};
